import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Spinner,
  VStack,
  HStack,
  Image,
  Flex,
  Container,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  IoCalendarOutline,
  IoEyeOutline,
} from "react-icons/io5";

import ApiResponse from "../../services/ApiResponse";
import { Noticia } from "../../services/models/Noticia";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";
import MapConfig from "../../MapConfig";

/* =======================
   Helpers
======================= */
const formatDate = (dateISO?: string | null) => {
  if (!dateISO) return "No publicada";
  const date = new Date(dateISO);
  return isNaN(date.getTime())
    ? "Fecha inválida"
    : date.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const getEstadoColor = (estado: Noticia["estado"]) => {
  switch (estado) {
    case 1:
      return "green";
    case 2:
      return "yellow";
    case 3:
      return "red";
    default:
      return "gray";
  }
};

/* =======================
   Page
======================= */
const NoticiaPage = () => {
  const { id } = useParams();
  const [response, setResponse] = useState(new ApiResponse<Noticia>());

  useEffect(() => {
    const fetchNoticia = async () => {
      const apiResponse = new ApiResponse<Noticia>();
      await apiResponse.useFetch(`noticias/${id}`, "GET");
      setResponse(apiResponse);
    };
    fetchNoticia();
  }, [id]);

  const { data, loading, responseCode } = response;

  /* =======================
     Estados
  ======================= */
  if (loading)
    return (
      <Flex justify="center" align="center" h="100vh" bg="blue.900">
        <Spinner size="xl" color="orange.400" thickness="4px" />
      </Flex>
    );

  if (responseCode === 401) return <UnauthorizedPage />;
  if (responseCode !== 200 || !data) return <ServerErrorPage />;

  /* =======================
     Render
  ======================= */
  return (
    <Box
      bgGradient="linear(to-b, blue.900, black)"
      minH="100vh"
      py={10}
    >
      <Container maxW="container.md">
        <VStack spacing={8} align="stretch">
          {/* ===== Cabecera periodística ===== */}
          <VStack align="start" spacing={3} color="white">
            <HStack spacing={2} fontSize="sm" color="whiteAlpha.600">
              <Badge colorScheme={getEstadoColor(data.estado)} fontSize="xs">
                {data.estado}
              </Badge>
              <Text>•</Text>
              <Text>{data.visibilidad}</Text>
              <Text>•</Text>
              <Icon as={IoCalendarOutline} />
              <Text>{formatDate(data.fecha_publicacion)}</Text>
            </HStack>

            <Heading as="h1" size="2xl" lineHeight="shorter">
              {data.titulo}
            </Heading>

            <Text fontSize="xl" color="whiteAlpha.800">
              {data.resumen}
            </Text>
          </VStack>

          {/* ===== Imagen destacada ===== */}
          <Box
            bg="blackAlpha.800"
            borderRadius="md"
            overflow="hidden"
          >
            <Image
              src={
                data.imagen
                  ? `${MapConfig.API_IMAGES_URL}${
                      data.imagen.startsWith("/")
                        ? data.imagen
                        : `/${data.imagen}`
                    }`
                  : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR4Ni5wwO02H47WSTi7PG2V-WuYZUXTfXGpmw&s"
              }
              alt={data.titulo}
              w="100%"
              maxH="480px"
              objectFit="cover"
              onError={(e) => {
                e.currentTarget.src =
                  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR4Ni5wwO02H47WSTi7PG2V-WuYZUXTfXGpmw&s";
              }}
            />
            <Text fontSize="sm" color="whiteAlpha.600" p={2}>
              Imagen ilustrativa
            </Text>
          </Box>

          {/* ===== Contenido ===== */}
          <Box
            bg="white"
            p={{ base: 6, md: 10 }}
            borderRadius="md"
          >
            <Text
              fontSize="lg"
              lineHeight="tall"
              whiteSpace="pre-line"
              color="gray.800"
            >
              {data.contenido}
            </Text>
          </Box>

          {/* ===== Meta info ===== */}
          <Divider borderColor="whiteAlpha.300" />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <MetaBox
              icon={IoCalendarOutline}
              label="Creada"
              value={formatDate(data.created_at)}
            />
            <MetaBox
              icon={IoEyeOutline}
              label="Visibilidad"
              value={data.visibilidad}
            />
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

/* =======================
   Sub-components
======================= */
const MetaBox = ({
  icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) => (
  <HStack
    bg="whiteAlpha.50"
    border="1px solid"
    borderColor="whiteAlpha.200"
    p={4}
    borderRadius="md"
    spacing={4}
  >
    <Flex bg="orange.400" p={2} borderRadius="md" color="blue.900">
      <Icon as={icon} boxSize={5} />
    </Flex>
    <VStack align="start" spacing={0}>
      <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase">
        {label}
      </Text>
      <Text fontWeight="bold" color="white">
        {value}
      </Text>
    </VStack>
  </HStack>
);

export default NoticiaPage;
